import { isCloudinaryUrl } from "@/scott/ui/elements/image/cloudinary";
import type { NavigationCartItem } from "@/scott/ui/layout/main-navigation/navigation-cart/navigation-cart";
import { getStorefrontBasePath } from "./accelerator-cart.utils";

const QUANTITY_PATTERN = /:\s*(\d+)\s*$/;

/** Reads the same browser session cart as add-to-cart and the header badge. */
export async function fetchAcceleratorCartItems(
  country: string,
  language: string
): Promise<NavigationCartItem[]> {
  const response = await fetch(
    `${getStorefrontBasePath(country, language)}/cart/rollover/MiniCart`,
    {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Unable to load the storefront cart");
  }

  // Parse the accelerator's existing fragment as inert data; never inject its
  // HTML or execute scripts. Its formatted price is the line total, not unit
  // price. A discounted entry crosses out .actual-price and adds .off-price.
  const document = new DOMParser().parseFromString(
    await response.text(),
    "text/html"
  );
  return Array.from(document.querySelectorAll(".mini-cart-item")).flatMap(
    (entry, index) => {
      const name = entry
        .querySelector(".minicart__product_title")
        ?.textContent?.trim();
      const href = safeUrl(
        entry.querySelector("a[href]")?.getAttribute("href")
      );
      const quantityText =
        entry.querySelector(".minicart__product_quantity")?.textContent ?? "";
      const quantity = Number(QUANTITY_PATTERN.exec(quantityText)?.[1]);
      if (!(name && href && Number.isSafeInteger(quantity) && quantity > 0)) {
        return [];
      }
      const image = entry.querySelector(".minicart__product_img img");
      const src = safeUrl(image?.getAttribute("src"));
      return [
        {
          id: `${href}-${index}`,
          name,
          href,
          image: src
            ? {
                // The accelerator thumbnail is only 70px. Let Image derive
                // responsive widths from the original for the 150px drawer slot.
                src: isCloudinaryUrl(src)
                  ? src.replace("/t_assetformatthumbnail/", "/")
                  : src,
                alt: image?.getAttribute("alt") || name,
              }
            : undefined,
          variantDescription: entry
            .querySelector(".minicart__product_subtitle")
            ?.textContent?.replace(/\s+/g, " ")
            .trim(),
          quantity,
          unitPrice: "",
          totalPrice:
            (
              entry.querySelector(".minicart__product_price .off-price") ??
              entry.querySelector(".minicart__product_price .actual-price")
            )?.textContent?.trim() ?? "",
        },
      ];
    }
  );
}

function safeUrl(value: string | null | undefined): string | undefined {
  if (!value) {
    return;
  }
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "https:" || url.protocol === "http:"
      ? value
      : undefined;
  } catch {
    return;
  }
}
